import React from 'react';
import {AdminUserProfile} from '../../domain/admin';
import {AdminBadge, NoPasswordBadge, SubscriptionBadge} from './AdminBadges';
import {
  Field,
  FieldList,
  PaddleLink,
  Panel,
  Timestamp,
  formatCount,
  formatPeriod,
  formatSeconds,
} from './AdminPrimitives';
import {AlertTriangleIcon} from '../Icons';

/**
 * The four cards at the top of a user's page: who they are, what they are entitled to, what they
 * have spent this period, and what they have spent ever.
 *
 * The subscription card is the one with an opinion. It leads with the **derived** state, which is
 * what the app actually enforces, and files the raw Paddle columns underneath under a heading that
 * says they lag — with a warning when the two disagree, because that disagreement is either a
 * lapsed subscription (normal) or a reconcile that has not run (worth knowing).
 */

interface AdminUserSummaryProps {
  profile: AdminUserProfile;
}

const AdminUserSummary: React.FC<AdminUserSummaryProps> = ({profile}) => {
  const {user, subscription, usage, lifetime, projects} = profile;

  const mirrorDisagrees
    = subscription.subscribed !== subscription.isSubscriptionActive
    || subscription.subscriptionPlan !== subscription.effectiveSubscriptionPlan;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="Account">
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {user.isAdmin && <AdminBadge />}
          {!user.passwordSet && <NoPasswordBadge />}
        </div>

        <FieldList>
          <Field label="Name">{user.name || <span className="text-gray-600">—</span>}</Field>
          <Field label="Email">{user.email}</Field>
          <Field label="User ID" mono hint="public.users.id — what every admin route takes">
            {user.id}
          </Field>
          <Field label="Supabase auth ID" mono>
            {user.supabaseUserId || <span className="text-gray-600">—</span>}
          </Field>
          <Field label="Password set" hint="False = provisioned by checkout, never chose a password">
            {user.passwordSet ? 'Yes' : <span className="text-amber-300">No</span>}
          </Field>
          <Field label="Welcome page">{user.welcomePage ? 'Shown' : 'Dismissed'}</Field>
          <Field label="Joined">
            <Timestamp iso={user.createdAt} />
          </Field>
          <Field label="Updated">
            <Timestamp iso={user.updatedAt} />
          </Field>
          <Field label="Projects">
            {!projects || projects.length === 0 ? (
              <span className="text-gray-600">None</span>
            ) : (
              projects.map(project => project.name + (project.isDefault ? ' (default)' : '')).join(', ')
            )}
          </Field>
        </FieldList>
      </Panel>

      <Panel
        title="Subscription"
        subtitle="Read-only here — a Paddle reconcile would revert any manual change within minutes."
      >
        <div className="mb-3 flex items-center gap-2">
          <SubscriptionBadge
            effectivePlan={subscription.effectiveSubscriptionPlan}
            isActive={subscription.isSubscriptionActive}
          />
          <span className="text-xs text-gray-500">
            {subscription.isSubscriptionActive ? 'Entitled' : 'Not entitled'}
          </span>
        </div>

        {mirrorDisagrees && (
          <p className="mb-3 flex items-start gap-1.5 rounded-lg border border-amber-500/30
           bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-200">
            <AlertTriangleIcon className="mt-0.5 h-3 w-3 shrink-0" />
            The Paddle mirror below disagrees with the derived state. Normal for a lapsed
            subscription; if it is not lapsed, the reconcile has not caught up.
          </p>
        )}

        <FieldList>
          <Field label="Effective plan" hint="Derived at query time — what the app enforces">
            {subscription.effectiveSubscriptionPlan}
          </Field>
          <Field label="Active" hint="Derived at query time">
            {subscription.isSubscriptionActive ? 'Yes' : 'No'}
          </Field>
        </FieldList>

        <p className="mt-3 border-t border-gray-800 pt-2 text-[11px] uppercase tracking-wide text-gray-600">
          Paddle mirror — lags reality
        </p>
        <FieldList>
          <Field label="subscribed">{subscription.subscribed ? 'true' : 'false'}</Field>
          <Field label="subscription_plan">{subscription.subscriptionPlan}</Field>
          <Field label="Expires">
            <Timestamp iso={subscription.subscriptionExpiresAt} fallback="—" />
          </Field>
          <Field label="Last synced">
            <Timestamp iso={subscription.lastSyncedAt} fallback="Never" />
          </Field>
          <Field label="Paddle customer">
            <PaddleLink kind="customer" id={subscription.paddleCustomerId} />
          </Field>
          <Field label="Paddle subscription">
            <PaddleLink kind="subscription" id={subscription.paddleSubscriptionId} />
          </Field>
        </FieldList>
      </Panel>

      <Panel
        title="Current billing period"
        subtitle={
          usage
            ? formatPeriod(usage.periodStart, usage.periodEnd)
            : 'Anchored on their renewal or signup date, not the calendar month.'
        }
      >
        {/*
          A null usage block means the plan limits failed to load. Rendering zeros there would read
          as "they have used nothing", which is the opposite of "we do not know".
        */}
        {!usage ? (
          <p className="py-2 text-xs text-gray-500">
            Usage unavailable — the plan limits failed to load for this user. This is not a zero.
          </p>
        ) : (
          <>
            <div className="mb-3">
              <p className="text-2xl font-semibold tabular-nums text-gray-50">
                {formatSeconds(usage.secondsUsed)}
                <span className="ml-1 text-sm font-normal text-gray-500">
                  {usage.secondsLimit === null ? 'used (unlimited)' : `of ${formatSeconds(usage.secondsLimit)}`}
                </span>
              </p>
              {usage.secondsLimit !== null && (
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-800">
                  <div
                    className={`h-full rounded-full ${
                      (usage.secondsRemaining ?? 0) <= 0
                        ? 'bg-red-500'
                        : (usage.secondsRemaining ?? 0) < usage.secondsLimit * 0.2
                          ? 'bg-amber-500'
                          : 'bg-emerald-500'
                    }`}
                    style={{
                      width: `${Math.min(100, Math.round((usage.secondsUsed / Math.max(1, usage.secondsLimit)) * 100))}%`,
                    }}
                  />
                </div>
              )}
            </div>

            <FieldList>
              <Field label="Plan (as billed)">{usage.plan}</Field>
              <Field label="Remaining">
                {usage.secondsRemaining === null ? 'Unlimited' : formatSeconds(usage.secondsRemaining)}
              </Field>
              <Field label="Per-file cap">
                {usage.maxFileSeconds === null ? 'None' : formatSeconds(usage.maxFileSeconds)}
              </Field>
              <Field label="Period start">
                <Timestamp iso={usage.periodStart} />
              </Field>
              <Field label="Period end">
                <Timestamp iso={usage.periodEnd} />
              </Field>
            </FieldList>
          </>
        )}
      </Panel>

      <Panel title="Lifetime">
        <FieldList>
          <Field label="Transcription time" hint="Lifetime billable seconds">
            {formatSeconds(lifetime.transcriptionSecondsTotal)}
          </Field>
          <Field label="Transcriptions" hint="Excludes soft-deleted rows">
            {formatCount(lifetime.transcriptionCount)}
          </Field>
          <Field label="In flight">
            {(lifetime.transcriptionsInFlight ?? 0) > 0 ? (
              <span className="text-indigo-300">{lifetime.transcriptionsInFlight}</span>
            ) : (
              '0'
            )}
          </Field>
          <Field label="Failed">
            {(lifetime.transcriptionsFailed ?? 0) > 0 ? (
              <span className="text-red-300">{lifetime.transcriptionsFailed}</span>
            ) : (
              '0'
            )}
          </Field>
          <Field label="Conversions" hint="Free and unlimited">
            {formatCount(lifetime.conversionCount)}
          </Field>
          <Field label="Audio uploads">{formatCount(lifetime.audioUploadCount)}</Field>
          <Field label="Projects">{formatCount(lifetime.projectCount)}</Field>
          <Field label="Last transcription">
            <Timestamp iso={lifetime.lastTranscriptionAt} fallback="Never" />
          </Field>
          <Field label="Last conversion">
            <Timestamp iso={lifetime.lastConversionAt} fallback="Never" />
          </Field>
        </FieldList>
      </Panel>
    </div>
  );
};

export default AdminUserSummary;
